import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Fonction utilitaire pour nettoyer la session locale
  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      console.log('[KadmiAi Auth] Fetching user profile...');
      // L'interceptor axios (services/api.js) gère automatiquement le refresh sur 401.
      // On ne refait PAS de retry manuel ici pour éviter une double rotation des tokens.
      const response = await api.get('/auth/me');
      console.log('[KadmiAi Auth] User profile loaded:', response.data?.email);
      setUser(response.data);
    } catch (error) {
      const status = error?.response?.status;
      console.error('[KadmiAi Auth] Failed to fetch user:', status, error?.message);
      // À ce stade : soit le refresh a aussi échoué (interceptor), soit erreur réseau.
      // Si 401 ferme : on nettoie la session locale (l'interceptor a déjà tenté un refresh).
      if (status === 401) {
        clearSession();
      }
      // Pour les erreurs réseau (offline, 502/503), on garde la session locale
      // pour éviter de virer l'utilisateur d'un simple souci passager.
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Recharge le profil quand l'onglet redevient actif (utile pour syncer le plan après upgrade Stripe)
  useEffect(() => {
    if (!token) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        api.get('/auth/me').then((r) => setUser(r.data)).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [token]);

  const login = async (email, password) => {
    console.log('[KadmiAi Auth] Attempting login for:', email);
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    console.log('[KadmiAi Auth] Login successful, storing tokens...');
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (nom, prenom, email, password, role = 'particulier') => {
    // Récupérer UTM depuis sessionStorage (posé par les landing pages TikTok/PH/etc.)
    let utm_source = null;
    let utm_campaign = null;
    try {
      utm_source = sessionStorage.getItem('utm_source');
      utm_campaign = sessionStorage.getItem('utm_campaign');
    } catch (_) { /* noop */ }
    const payload = { nom, prenom, email, password, role };
    if (utm_source) payload.utm_source = utm_source;
    if (utm_campaign) payload.utm_campaign = utm_campaign;
    const response = await api.post('/auth/register', payload);
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    console.log('[KadmiAi Auth] Logging out...');
    // Best-effort: invalider côté serveur. Ne pas bloquer si erreur réseau.
    try { await api.post('/auth/logout'); } catch (_) { /* noop */ }
    clearSession();
  };

  const updateProfile = async (updates) => {
    const response = await api.put('/auth/profile', updates);
    setUser(response.data);
    return response.data;
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('[KadmiAi Auth] refreshUser failed:', error?.response?.status);
      return null;
    }
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated,
      login, 
      register, 
      logout, 
      updateProfile, 
      refreshUser, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
